import React from "react";
import {
  Compass, BookOpen, Flame, Sparkles, TreePine, Feather, Target,
  Clock, KeyRound, Sunrise,
} from "lucide-react";
import {
  Button,
  ParchmentCard,
  JourneyCard,
  CollectionOval,
  PanelRow,
  SanskritSubtitle,
} from "../design-system";

/**
 * Design System · Living Reference
 *
 * Private route (`/design-system`) that renders every component in
 * every state. Used as visual QA for subsequent pages. Not linked
 * from any public nav.
 */
export default function DesignSystemPage() {
  return (
    <main
      style={{
        minHeight: "100vh",
        background: "var(--icz-parchment-200)",
        padding: "var(--icz-s-8) var(--icz-s-5)",
        color: "var(--icz-ink-300)",
      }}
      data-testid="design-system-page"
    >
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <p className="icz-eyebrow">Sprint 1 · Increment 1</p>
        <h1 className="icz-display">iCRAFTZ · Design System</h1>
        <p className="icz-body" style={{ maxWidth: 640, marginTop: "var(--icz-s-4)" }}>
          Every subsequent page inherits its typography, spacing, colours, interaction
          behaviour, and visual language from these primitives. The Homepage is the
          canonical reference; this page is where the primitives themselves are
          demonstrated in every state.
        </p>

        <Section title="Typography" sanskrit="Akshara">
          <p className="icz-display-xl">Display · XL</p>
          <p className="icz-display">Display</p>
          <p className="icz-h1">Heading · H1</p>
          <p className="icz-h2">Heading · H2</p>
          <p className="icz-h3">Heading · H3</p>
          <p className="icz-eyebrow">Eyebrow · caps</p>
          <p className="icz-tagline">Tagline · Where you craft who you become</p>
          <p className="icz-body">Body · Timeless wisdom from the world's greatest books.</p>
          <p className="icz-body-sm">Body small · One idea at a time. One habit at a time.</p>
          <p className="icz-caption">Caption · footnote / metadata</p>
          <SanskritSubtitle>Sanskrit subtitle — Sadhana</SanskritSubtitle>
        </Section>

        <Section title="Buttons" sanskrit="Karma">
          <Row>
            <Button variant="primary" size="sm">Primary · SM</Button>
            <Button variant="primary" size="md">Primary · MD</Button>
            <Button variant="primary" size="lg">Primary · LG</Button>
            <Button variant="primary" disabled>Disabled</Button>
          </Row>
          <Row>
            <Button variant="ghost" size="sm">Ghost · SM</Button>
            <Button variant="ghost" size="md">Ghost · MD</Button>
            <Button variant="ghost" size="lg">Ghost · LG</Button>
          </Row>
          <Row>
            <Button variant="link">Learn more</Button>
            <Button variant="primary" size="md">
              Begin Your Journey <Sparkles size={14} />
            </Button>
          </Row>
        </Section>

        <Section title="Parchment Card" sanskrit="Patra">
          <Row wrap>
            <ParchmentCard style={{ width: 260 }} elevation="flat">
              <p className="icz-eyebrow">Flat</p>
              <p className="icz-body-sm">Subtlest surface. Use for dense lists.</p>
            </ParchmentCard>
            <ParchmentCard style={{ width: 260 }}>
              <p className="icz-eyebrow">Default</p>
              <p className="icz-body-sm">Standard elevation for content blocks.</p>
            </ParchmentCard>
            <ParchmentCard style={{ width: 260 }} elevation="elevated" grain>
              <p className="icz-eyebrow">Elevated · grain</p>
              <p className="icz-body-sm">For hero surfaces. Grain adds texture.</p>
            </ParchmentCard>
            <ParchmentCard style={{ width: 260 }} interactive as="button">
              <p className="icz-eyebrow">Interactive</p>
              <p className="icz-body-sm">Hover-lift + focus ring baked in.</p>
            </ParchmentCard>
          </Row>
        </Section>

        <Section title="Journey Cards" sanskrit="Yatra">
          <Row wrap>
            <div style={{ width: 200 }}>
              <JourneyCard
                icon={<Compass size={30} />}
                english="Discover"
                sanskrit="Jigyasa"
                subcopy="Find ideas worth exploring"
                onClick={() => {}}
              />
            </div>
            <div style={{ width: 200 }}>
              <JourneyCard
                icon={<BookOpen size={30} />}
                english="Learn"
                sanskrit="Bodha"
                subcopy="Understand what truly matters"
                onClick={() => {}}
              />
            </div>
            <div style={{ width: 200 }}>
              <JourneyCard
                icon={<Flame size={30} />}
                english="Commit"
                sanskrit="Sankalpa"
                subcopy="Choose one idea to live by"
                state="active"
                onClick={() => {}}
              />
            </div>
            <div style={{ width: 200 }}>
              <JourneyCard
                icon={<Sparkles size={30} />}
                english="Practice"
                sanskrit="Sadhana"
                subcopy="Apply daily with discipline"
                state="disabled"
              />
            </div>
            <div style={{ width: 200 }}>
              <JourneyCard
                icon={<TreePine size={30} />}
                english="Transform"
                sanskrit="Siddhi"
                subcopy="Become who you want to be"
                onClick={() => {}}
              />
            </div>
          </Row>
        </Section>

        <Section title="Collection Ovals" sanskrit="Vartula">
          <Row wrap>
            <CollectionOval icon={<Compass size={40} />} title="Habits" onClick={() => {}} />
            <CollectionOval icon={<BookOpen size={40} />} title="Focus & Deep Work" onClick={() => {}} />
            <CollectionOval icon={<Sparkles size={40} />} title="Wealth & Money" onClick={() => {}} />
            <CollectionOval icon={<Target size={40} />} title="Mindset & Growth" onClick={() => {}} />
            <CollectionOval icon={<Flame size={40} />} title="Leadership" onClick={() => {}} />
            <CollectionOval icon={<TreePine size={40} />} title="Purpose & Life" onClick={() => {}} />
          </Row>
        </Section>

        <Section title="Panel Rows" sanskrit="Pankti">
          <ParchmentCard style={{ maxWidth: 460, padding: "var(--icz-s-3) var(--icz-s-4)" }}>
            <PanelRow
              icon={<Feather size={18} />}
              title="AI-Curated Wisdom Artifacts"
              description="Extracted from the world's best books"
              onClick={() => {}}
            />
            <PanelRow
              icon={<Target size={18} />}
              title="Rule of Three"
              description="Focus on what truly transforms"
              onClick={() => {}}
            />
            <PanelRow
              icon={<KeyRound size={18} />}
              title="CraftPrint™"
              description="Your 30-day personalized action plan"
              onClick={() => {}}
            />
            <PanelRow
              icon={<Flame size={18} />}
              title="Daily Sadhana"
              description="Build habits. Build identity."
              onClick={() => {}}
            />
            <PanelRow
              icon={<Sunrise size={18} />}
              title="My Sanctuary"
              description="Your space for reflections and insights"
              onClick={() => {}}
            />
            <PanelRow
              icon={<TreePine size={18} />}
              title="Siddhi"
              description="Measure transformation, not just progress"
              onClick={() => {}}
            />
          </ParchmentCard>
        </Section>

        <Section title="Sanskrit Subtitle" sanskrit="Upashirsha">
          <p className="icz-h2">Learn</p>
          <SanskritSubtitle size="md" align="left">Bodha</SanskritSubtitle>
          <div style={{ height: 12 }} />
          <p className="icz-h3">Commit</p>
          <SanskritSubtitle size="sm" align="left">Sankalpa</SanskritSubtitle>
        </Section>

        <div style={{ marginTop: "var(--icz-s-8)", paddingTop: "var(--icz-s-6)", borderTop: "1px solid var(--icz-parchment-500)", opacity: 0.7 }}>
          <p className="icz-caption">
            Design System · v0.1 · Sprint 1 · Increment 1 · iCRAFTZ · 2026-07-04
          </p>
        </div>
      </div>
    </main>
  );
}

function Section({ title, sanskrit, children }) {
  return (
    <section
      data-testid={`ds-section-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      style={{
        marginTop: "var(--icz-s-8)",
        paddingTop: "var(--icz-s-6)",
        borderTop: "1px solid rgba(139, 101, 32, 0.25)",
      }}
    >
      <p className="icz-eyebrow">{title}</p>
      <SanskritSubtitle align="left">{sanskrit}</SanskritSubtitle>
      <div style={{ marginTop: "var(--icz-s-5)", display: "flex", flexDirection: "column", gap: "var(--icz-s-4)" }}>
        {children}
      </div>
    </section>
  );
}

function Row({ children, wrap }) {
  return (
    <div
      style={{
        display: "flex",
        gap: "var(--icz-s-4)",
        alignItems: "center",
        flexWrap: wrap ? "wrap" : "nowrap",
      }}
    >
      {children}
    </div>
  );
}
